// SmoothStickySection.jsx
import React, { useEffect, useRef, useState } from "react";
import "./ourvision.css";
import TextAnimation from "../../hooks/TextAnimation";

const OurVision = () => {
  const sectionRef = useRef(null);
  const bgRef = useRef(null);
  const contentRef = useRef(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [expandedCard, setExpandedCard] = useState(null);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState("outside");
  const [bgTransform, setBgTransform] = useState({
    scale: 1,
    translateY: 100,
    opacity: 0,
    blur: 10,
  });

  const cardData = [
    {
      id: "card-smooth-001",
      title: "Innovation Lab",
      shortDesc: "Cutting-edge research facility",
      fullDesc:
        "Our Innovation Lab is where ideas transform into reality. With a team of 200+ researchers and 20% revenue investment in R&D, we've developed breakthrough technologies in AI, robotics, and sustainable materials.",
      features: [
        "AI Research",
        "Robotics Development",
        "Material Science",
        "50+ Patents",
      ],
      stats: ["200+ Researchers", "20% Revenue Investment", "50+ Patents"],
      image:
        "https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=1000&q=80",
      color: "#3B82F6",
      accentColor: "#1D4ED8",
    },
    {
      id: "card-smooth-002",
      title: "Global Network",
      shortDesc: "Worldwide presence",
      fullDesc:
        "Spanning 6 continents with strategic hubs in key locations, our global network delivers solutions to over 1,000 enterprises, bridging technological gaps across diverse markets and cultures.",
      features: [
        "50+ Countries",
        "6 Continents",
        "Global Hubs",
        "1000+ Clients",
      ],
      stats: ["50+ Countries", "6 Continental Hubs", "1000+ Enterprises"],
      image:
        "https://images.unsplash.com/photo-1542744095-fcf48d80b0fd?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=1000&q=80",
      color: "#10B981",
      accentColor: "#047857",
    },
    {
      id: "card-smooth-003",
      title: "Sustainability",
      shortDesc: "Eco-friendly solutions",
      fullDesc:
        "Pioneering green technology with 100% renewable energy usage and zero-waste manufacturing. Our carbon-neutral initiatives since 2020 have significantly reduced environmental impact.",
      features: [
        "100% Renewable",
        "Zero Waste",
        "Green Tech",
        "Carbon Neutral",
      ],
      stats: ["100% Renewable", "Zero Waste", "Since 2020"],
      image:
        "https://images.unsplash.com/photo-1521791136064-7986c2920216?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=1000&q=80",
      color: "#059669",
      accentColor: "#065F46",
    },
    {
      id: "card-smooth-004",
      title: "Work Culture",
      shortDesc: "Award-winning environment",
      fullDesc:
        'Recognized as "Best Place to Work" for 3 consecutive years. We foster innovation through flexible work arrangements, comprehensive benefits, and continuous learning opportunities.',
      features: [
        "Flexible Work",
        "Learning Budget",
        "Healthcare",
        "Award Winning",
      ],
      stats: ["3 Years Award", "98% Satisfaction", "25% Growth"],
      image:
        "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=1000&q=80",
      color: "#8B5CF6",
      accentColor: "#7C3AED",
    },
  ];

  const handleCardHover = (cardId) => {
    if (!expandedCard) {
      setHoveredCard(cardId);
    }
  };

  const handleCardClick = (cardId) => {
    if (expandedCard === cardId) {
      setExpandedCard(null);
      setHoveredCard(null);
    } else {
      setExpandedCard(cardId);
      setHoveredCard(cardId);
    }
  };

  const handleCardLeave = () => {
    if (!expandedCard) {
      setHoveredCard(null);
    }
  };

  const getCardClass = (cardId) => {
    if (expandedCard === cardId) return "card-expanded";
    if (hoveredCard === cardId) return "card-hovered";
    if (hoveredCard && hoveredCard !== cardId) return "card-shrunk";
    return "";
  };

  useEffect(() => {
    let rafId;

    const updateAnimations = () => {
      if (!sectionRef.current || !bgRef.current) return;

      const section = sectionRef.current;
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;

      const sectionTop = section.offsetTop;
      const sectionHeight = section.offsetHeight;

      const scrollDistance = scrollY - sectionTop + windowHeight * 0.5;
      const totalScrollable = sectionHeight + windowHeight * 0.5;
      const currentProgress = Math.max(
        0,
        Math.min(1, scrollDistance / totalScrollable)
      );
      setProgress(currentProgress);

      if (currentProgress < 0.15) {
        setPhase("entering");
        const phaseProgress = currentProgress / 0.15;
        const translateY = 100 - phaseProgress * 100;
        const opacity = phaseProgress;
        const scale = 0.9 + phaseProgress * 0.1;
        const blur = 10 - phaseProgress * 10;

        setBgTransform({ translateY, opacity, scale, blur });
        bgRef.current.style.position = "fixed";
        bgRef.current.style.top = "0";
      } else if (currentProgress >= 0.15 && currentProgress < 0.75) {
        setPhase("sticky");
        setBgTransform({
          translateY: 0,
          opacity: 1,
          scale: 1,
          blur: 0,
        });
        bgRef.current.style.position = "fixed";
        bgRef.current.style.top = "0";
      } else if (currentProgress >= 0.75 && currentProgress < 1) {
        setPhase("leaving");
        const fadeOutProgress = (currentProgress - 0.75) / 0.25;
        const translateY = -(fadeOutProgress * 50);
        const opacity = 1 - fadeOutProgress;
        const scale = 1 - fadeOutProgress * 0.1;
        const blur = fadeOutProgress * 5;

        setBgTransform({ translateY, opacity, scale, blur });
        if (currentProgress < 0.95) {
          bgRef.current.style.position = "fixed";
          bgRef.current.style.top = "0";
        } else {
          bgRef.current.style.position = "absolute";
          bgRef.current.style.top = "100%";
        }
      } else {
        setPhase("exited");
        setBgTransform({
          translateY: -100,
          opacity: 0,
          scale: 0.9,
          blur: 10,
        });
        bgRef.current.style.position = "absolute";
        bgRef.current.style.top = "100%";
      }

      if (contentRef.current) {
        const contentParallax = Math.min(100, currentProgress * 150);
        contentRef.current.style.transform = `translateY(${contentParallax}px)`;
      }

      rafId = requestAnimationFrame(updateAnimations);
    };

    rafId = requestAnimationFrame(updateAnimations);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  const features = [
    {
      title: "Innovation",
      description: "Pushing boundaries with cutting-edge technology solutions",
      icon: "🚀",
      color: "var(--accent-1)",
    },
    {
      title: "Quality",
      description: "Delivering excellence in every product and service",
      icon: "⭐",
      color: "var(--accent-2)",
    },
    {
      title: "Sustainability",
      description: "Building a greener future through responsible practices",
      icon: "🌱",
      color: "#1e8f75",
    },
  ];

  return (
    <div className="smooth-our-container">
      <section
        ref={sectionRef}
        className="smooth-our-section"
        style={{ minHeight: "auto" }} // or "auto"
        id="ourvision"
      >
        <div
          ref={bgRef}
          className="smooth-our-background"
          style={{
            transform: `translateY(${bgTransform.translateY}px) scale(${bgTransform.scale})`,
            opacity: bgTransform.opacity,
            filter: `blur(${bgTransform.blur}px)`,
            position: "fixed",
            top: "0",
          }}
        ></div>
        <div ref={contentRef} className="smooth-our-content">
          <div className="content-our-wrapper">
            <div className="smooth-our-header">
              <h1 className="main-our-title">
                <span className="title-our-word">Our Vision</span>
              </h1>
              <p className="our-subtitle">
                Building the future with innovation, quality, and sustainability
              </p>
            </div>
























            
            {/* Company Summary Section */}
            <div className="company-summary">
              <div className="smooth-cards-container">
                {/* <div className="cards-header"></div> */}
                <div className="cards-wrapper">
                  <div className="cards-row">
                    {cardData.map((card, index) => {
                      const cardClass = getCardClass(card.id);
                      const isActive =
                        hoveredCard === card.id || expandedCard === card.id;

                      return (
                        <div
                          key={card.id}
                          className={`smooth-card ${cardClass}`}
                          style={{ "--card-index": index }}
                          onMouseEnter={() => handleCardHover(card.id)}
                          onMouseLeave={handleCardLeave}
                          onClick={() => handleCardClick(card.id)}
                        >
                          {/* Main Image Cover */}
                          <div
                            className="card-image-cover"
                            style={{ backgroundImage: `url(${card.image})` }}
                          >
                            <div className="image-overlay"></div>
                            <div className="image-content">
                              <div className="card-badge">
                                <span className="badge-icon">★</span>
                                <span className="badge-text">Featured</span>
                              </div>

                              <h3 className="card-main-title">{card.title}</h3>
                              <p className="card-image-desc">
                                {card.shortDesc}
                              </p>

                              <div className="image-features">
                                {card.features.slice(0, 2).map((feature, i) => (
                                  <span key={i} className="image-feature">
                                    {feature}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* Expanded Content */}
                          <div
                            className={`card-expanded-content ${
                              isActive ? "expanded" : ""
                            }`}
                          >
                            <div className="expanded-main">
                              <div className="expanded-header">
                                <h3 className="expanded-title">
                                  <span
                                    className="title-highlight"
                                    style={{ backgroundColor: card.color }}
                                  >
                                    {card.title}
                                  </span>
                                </h3>
                                <div className="expanded-stats">
                                  {card.stats.map((stat, i) => (
                                    <div key={i} className="stat-item">
                                      <div
                                        className="stat-dot"
                                        style={{ backgroundColor: card.color }}
                                      ></div>
                                      <span className="stat-text">{stat}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              <div className="expanded-body">
                                <p className="expanded-description">
                                  {card.fullDesc}
                                </p>

                                <div className="feature-grid">
                                  {card.features.map((feature, i) => (
                                    <div
                                      key={i}
                                      className="feature-card"
                                      style={{ borderLeftColor: card.color }}
                                    >
                                      <span className="feature-icon">✓</span>
                                      <span className="feature-name">
                                        {feature}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              <div className="expanded-footer">
                                <button
                                  className="action-button"
                                  style={{
                                    backgroundColor: card.color,
                                    "--hover-color": card.accentColor,
                                  }}
                                >
                                  <span className="button-text">
                                    Discover More
                                  </span>
                                  <span className="button-arrow">→</span>
                                </button>
                                <div className="card-status">
                                  <span
                                    className="status-indicator"
                                    style={{ backgroundColor: card.color }}
                                  >
                                    {expandedCard === card.id
                                      ? "Expanded"
                                      : "Hovering"}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Hover Indicator */}
                          <div className="card-hover-indicator">
                            <div
                              className="indicator-line"
                              style={{ backgroundColor: card.color }}
                            ></div>
                            <span className="indicator-text">
                              {expandedCard === card.id
                                ? "Click to collapse"
                                : "Hover or click to expand"}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              {/* card */}























              

              {/* Company Features */}
              <div className="features-section">
                <h2 className="features-title">What Makes Us Different</h2>
                <div className="features-grid">
                  {features.map((feature, index) => (
                    <div
                      key={index}
                      className="transform-glow-border"
                      style={{
                        opacity: Math.max(0, Math.min(1, (progress - 0.1) * 3)),
                      }}
                    >
                      <div class="bottom-left-horizontal"></div>
                      <div class="bottom-left-vertical"></div>
                      <div
                        className="feature-icon-wrapper"
                        style={{ backgroundColor: feature.color }}
                      >
                        <div className="feature-icon">{feature.icon}</div>
                      </div>
                      <h3>{feature.title}</h3>
                      <p className="feature-description">
                        {feature.description}
                      </p>
                    </div>
                  ))}
                </div>
                <div class="glow-bottom-left"></div>
              </div>
              {/* CTA */}
              <div className="final-cta">
                <div class="bottom-left-horizontal"></div>
                <div class="bottom-left-vertical"></div>
                <TextAnimation
                  text="Join us in shaping the future"
                  type="word"
                  animationType="fade"
                  delay={0.2}
                />
                <p className="cta-subtitle">
                  Interested in working with us or learning more about our
                  solutions?
                </p>
                <button className="cta-button">Get in Touch</button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OurVision;
